import React, { useEffect, useRef, useMemo } from "react";
import Typography from "apollo-react/components/Typography";
import SubFormTab from "../../SubFormTab/SubFormTab";
import RequestBadges from "../../RequestBadges";
import styles from "../FormSections.module.css";
import { useFormRenderer, useSubFormApproval, useCurrentStageStatus } from "../../../../../hooks";
import { useMultiForm } from "../../../../../contexts/MultiFormContext";
import $ from "jquery";

// memoized custom form renderer to prevent flickering when parent re-renders
const CustomFormRenderer = React.memo(({
  activeFormId,
  editable,
  isSendbackMode,
  requestDetails,
  persona,
  roleName,
  requestProgress,
  requestId,
  customFormApprovalProps,
  ProductDetails,
  ProductDetailsView,
  ManufacturersSite,
  ManufacturersSiteView,
}) => {
  if (activeFormId === 'custom_product_details') {
    if (editable) {
      return <ProductDetails isSendbackMode={isSendbackMode} />;
    } else {
      return (
        <ProductDetailsView
          productDetailsData={requestDetails?.productDetails}
          persona={persona}
          isSendbackMode={isSendbackMode}
          role={roleName}
          requestProgressStatus={requestProgress?.status || ''}
          requestProgress={requestProgress}
          requestId={requestId}
          {...customFormApprovalProps}
        />
      );
    }
  }

  if (activeFormId === 'custom_manufacturers_site') {
    if (editable) {
      return <ManufacturersSite isSendbackMode={isSendbackMode} />;
    } else {
      return (
        <ManufacturersSiteView
          manufacturersSiteData={requestDetails?.manufacturersSite}
          persona={persona}
          isSendbackMode={isSendbackMode}
          role={roleName}
          requestProgressStatus={requestProgress?.status || ''}
          requestProgress={requestProgress}
          requestId={requestId}
          {...customFormApprovalProps}
        />
      );
    }
  }

  return null;
}, (prevProps, nextProps) => {
  // only re-render if critical props change
  return (
    prevProps.activeFormId === nextProps.activeFormId &&
    prevProps.editable === nextProps.editable &&
    prevProps.isSendbackMode === nextProps.isSendbackMode &&
    prevProps.requestDetails?.productDetails?.id === nextProps.requestDetails?.productDetails?.id &&
    prevProps.requestDetails?.manufacturersSite?.id === nextProps.requestDetails?.manufacturersSite?.id &&
    prevProps.persona === nextProps.persona &&
    prevProps.roleName === nextProps.roleName &&
    prevProps.requestProgress?.id === nextProps.requestProgress?.id &&
    prevProps.requestId === nextProps.requestId &&
    // don't compare customFormApprovalProps deeply - use ref equality
    prevProps.customFormApprovalProps === nextProps.customFormApprovalProps
  );
});

CustomFormRenderer.displayName = 'CustomFormRenderer';

// Helper function to display field errors below inputs
const displayFieldErrors = (container, subformId, fieldErrors) => {
  if (!container || !fieldErrors) return;
  
  Object.keys(fieldErrors).forEach(fieldName => {
    try {
      const fieldError = fieldErrors[fieldName];
      if (!fieldError || !fieldError.error) return;
      
      const input = $(container).find(`[name="${fieldName}"]`).first();
      
      if (input.length > 0) {
        // Remove any existing error message for this field
        input.siblings('.field-error-message').remove();
        
        // Create error message element with escaped content
        const errorText = String(fieldError.error).replace(/[<>&"']/g, (match) => {
          const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
          return entities[match];
        });
        
        const errorElement = $(`
          <div class="field-error-message" data-field="${fieldName}" style="
            color: #d32f2f;
            font-size: 12px;
            margin-top: 4px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 4px;
            line-height: 1.4;
          ">
            <span style="color: #d32f2f; flex-shrink: 0;">⚠</span>
            <span>${errorText}</span>
          </div>
        `);
        
        // Insert error message after the input or its wrapper
        // Look for common wrapper patterns
        const insertTarget = input.parent('.form-group, .input-group, .form-field, .field-wrapper').length > 0 
          ? input.parent('.form-group, .input-group, .form-field, .field-wrapper')
          : input;
        insertTarget.after(errorElement);
        
        // Add error styling to input
        input.css({
          'border-color': '#d32f2f',
          'box-shadow': '0 0 0 1px rgba(211, 47, 47, 0.2)'
        }).addClass('form-field-error');
      }
    } catch (error) {
      console.warn('Error displaying field error for', fieldName, error);
    }
  });
};

// Helper function to get field validation info from subform definition
const getFieldValidationInfo = (container, subformId) => {
  const validationMap = {};
  
  // Try to get subform definition from window if available
  if (window.getCurrentSubFormDefinition) {
    try {
      const subformDef = window.getCurrentSubFormDefinition(subformId);
      if (subformDef && subformDef.json_object) {
        const formFields = JSON.parse(subformDef.json_object);
        if (Array.isArray(formFields)) {
          formFields.forEach(field => {
            if (field && field.name) {
              validationMap[field.name] = {
                required: field.required || false,
                label: field.label || field.name
              };
            }
          });
        }
      }
    } catch (e) {
      // Fallback: extract validation info from DOM
    }
  }
  
  // Fallback: look for required attributes in DOM
  $(container).find('input, textarea, select').each(function() {
    const input = $(this);
    const fieldName = input.attr('name');
    if (fieldName && !validationMap[fieldName]) {
      const isRequired = input.attr('required') !== undefined || 
                        input.hasClass('required') ||
                        input.closest('.required').length > 0;
      
      validationMap[fieldName] = {
        required: isRequired,
        label: input.attr('placeholder') || input.prev('label').text() || fieldName
      };
    }
  });
  
  return validationMap;
};

// Helper function to attach real-time validation listeners
const attachRealTimeValidationListeners = (container, subformId) => {
  if (!container || !subformId) return;
  
  // Get validation info for this subform
  const validationMap = getFieldValidationInfo(container, subformId);
  
  $(container).find('input, textarea, select').each(function() {
    const input = $(this);
    const fieldName = input.attr('name');
    
    if (fieldName) {
      // Remove existing listeners to avoid duplicates
      input.off('input.errorValidate change.errorValidate blur.errorValidate focus.errorValidate');
      
      // Track if field was interacted with to avoid showing errors on initial load
      let hasBeenTouched = false;
      
      // Add listeners for real-time validation (both showing and clearing errors)
      input.on('focus.errorValidate', function() {
        hasBeenTouched = true;
      });
      
      input.on('input.errorValidate change.errorValidate blur.errorValidate', function(e) {
        try {
          const value = $(this).val();
          const trimmedValue = String(value || '').trim();
          const fieldValidation = validationMap[fieldName];
          
          // Only validate if field has been touched or on blur events
          // This prevents showing errors immediately on page load
          if (!hasBeenTouched && e.type !== 'blur') {
            return;
          }
          
          // Check if field should have an error
          const shouldHaveError = fieldValidation && fieldValidation.required && trimmedValue === '';
          const currentlyHasError = input.hasClass('form-field-error');
          
          if (shouldHaveError && !currentlyHasError) {
            // Show error
            const errorMessage = `${fieldValidation.label} is required`;
            const errorText = String(errorMessage).replace(/[<>&"']/g, (match) => {
              const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
              return entities[match];
            });
            
            const errorElement = $(`
              <div class="field-error-message" data-field="${fieldName}" style="
                color: #d32f2f;
                font-size: 12px;
                margin-top: 4px;
                margin-bottom: 8px;
                display: flex;
                align-items: center;
                gap: 4px;
                line-height: 1.4;
              ">
                <span style="color: #d32f2f; flex-shrink: 0;">⚠</span>
                <span>${errorText}</span>
              </div>
            `);
            
            // Insert error message after the input or its wrapper
            // Look for common wrapper patterns
            const insertTarget = input.parent('.form-group, .input-group, .form-field, .field-wrapper').length > 0 
              ? input.parent('.form-group, .input-group, .form-field, .field-wrapper')
              : input;
            insertTarget.after(errorElement);
            
            // Add error styling to input
            input.css({
              'border-color': '#d32f2f',
              'box-shadow': '0 0 0 1px rgba(211, 47, 47, 0.2)'
            }).addClass('form-field-error');
            
            // Notify context to add the error
            if (window.addFieldError && typeof window.addFieldError === 'function') {
              window.addFieldError(subformId, fieldName, {
                label: fieldValidation.label,
                error: errorMessage,
                required: true
              });
            }
            
          } else if (!shouldHaveError && currentlyHasError) {
            // Clear error
            $(container).find(`.field-error-message[data-field="${fieldName}"]`).remove();
            
            input.css({
              'border-color': '',
              'box-shadow': ''
            }).removeClass('form-field-error');
            
            // Notify context to clear the error
            if (window.clearFieldError && typeof window.clearFieldError === 'function') {
              window.clearFieldError(subformId, fieldName);
            }
          }
        } catch (error) {
          console.warn('Error in field validation for', fieldName, error);
        }
      });
    }
  });
};

// New component that renders only the active form
export const FormContent = ({ 
  forms, 
  activeTab, 
  filledForm,
  handleAddComment,
  requestProgressId,
  persona,
  requestId,
  enableAddComment = true,
  children,
  requestProgress,
  disabled = true,
  errors = null,
}) => {
  const containerRef = useRef(null);
  const { renderForm } = useFormRenderer();
  const renderedFormIdRef = useRef(null);
  const { data: currentStageStatus } = useCurrentStageStatus(requestId, persona);
  
  // Find the active form
  const activeForm = forms.find(form => form.id === activeTab);
  
  useEffect(() => {
    if (!activeForm || !containerRef.current || !filledForm) return;
    
    const currentContainer = containerRef.current;
    
    // Only re-render if we're switching to a different form
    if (renderedFormIdRef.current === activeForm.id) {
      return; // Skip re-rendering if it's the same form
    }
    
    renderedFormIdRef.current = activeForm.id;
    
    renderForm({
      form: activeForm,
      container: currentContainer,
      filledForm,
      handleAddComment,
      requestProgressId,
      persona,
      requestId,
      enableAddComment,
      showCommentsTitle: false,
      requestProgress,
      children,
      disabled,
      currentStageStatus,
    });

    // Add field errors and real-time validation after form rendering
    setTimeout(() => {
      if (errors?.subforms?.[activeForm.id]?.fields) {
        displayFieldErrors(currentContainer, activeForm.id, errors.subforms[activeForm.id].fields);
      }
      // Always attach validation listeners for real-time feedback
      attachRealTimeValidationListeners(currentContainer, activeForm.id);
    }, 100);
    
    // Cleanup when switching tabs
    return () => {
      if (currentContainer && renderedFormIdRef.current !== activeForm.id) {
        currentContainer.innerHTML = '';
        renderedFormIdRef.current = null;
      }
    };
  }, [activeForm, currentStageStatus, filledForm, handleAddComment, requestProgressId, persona, requestId, enableAddComment, renderForm, disabled, requestProgress, children, errors]);
  
  return (
    <>
      <div className="vertical-tab-panel">
        <div
          className="form-section-container"
          ref={containerRef}
        />
      </div>
      {children}
    </>
  );
};

// Base layout component with support for custom content rendering
export const FormSectionsLayout = ({
  forms,
  activeTab,
  onTabChange,
  isLoading: externalLoadingState,
  prevComments,
  requestDetails,
  requestProgressId,
  persona,
  requestProgress,
  children,
  customContent = false,
  headerTitle = "Main Menu",
  showRequestBadges = true,
  filledForm,
  handleAddComment,
  requestId,
  enableAddComment = true,
  subformActionButtons = null,
  formActionButtons = null,
  formsWithApprovalStatus = null,
  editable = false,
  onFormDataChange = null,
  isCheckingApprovals = false,
  skipApprovalFetch = false, // Flag to skip internal approval fetching
  errors = null,
  hasProductDetails = false,
  hasManufacturersSite = false,
  isSendbackMode = false,
  productDetailsData = null,
  roleName = '', // Add roleName for passing to custom form views
  customFormApprovalProps = null, // Approval props for custom forms
}) => {
  // Only use the hook if not skipping and no approval status provided
  const shouldFetchInternally = !skipApprovalFetch && !formsWithApprovalStatus;
  
  const { 
    subForms: subFormsWithApproval,
    isCheckingApprovals: internalCheckingApprovals,
  } = useSubFormApproval({
    enduserFilledFormId: shouldFetchInternally ? filledForm?.id : null,
    subForms: shouldFetchInternally ? forms : [],
    persona: shouldFetchInternally ? persona : null
  });

  // Use provided forms with approval status, or internally fetched ones
  const formsToRender = formsWithApprovalStatus || (shouldFetchInternally ? subFormsWithApproval : forms);

  // Use external loading state if provided, otherwise use internal checking state
  const isLoading = externalLoadingState !== undefined 
    ? externalLoadingState 
    : (skipApprovalFetch ? isCheckingApprovals : internalCheckingApprovals);

  // check if active tab is a custom form
  const activeForm = formsToRender.find(form => form.id === activeTab);
  const isCustomForm = activeForm?.isCustom === true;

  // dynamically import ProductDetails components - memoize to prevent re-imports
  const ProductDetails = useMemo(() => React.lazy(() => import('../../../../Guest/Requests/New_Requests/ProductDetails/ProductDetails')), []);
  const ProductDetailsView = useMemo(() => React.lazy(() => import('../../../../Guest/Requests/New_Requests/ProductDetails/ProductDetailsView')), []);

  // dynamically import ManufacturersSite components - memoize to prevent re-imports
  const ManufacturersSite = useMemo(() => React.lazy(() => import('../../../../Guest/Requests/New_Requests/ManufacturersSite/ManufacturersSite')), []);
  const ManufacturersSiteView = useMemo(() => React.lazy(() => import('../../../../Guest/Requests/New_Requests/ManufacturersSite/ManufacturersSiteView')), []);

  if (!formsToRender || formsToRender.length === 0) {
    return (
      <div className={styles.verticalTabsContainer}>
        <Typography style={{ padding: "1rem" }}>
          There are no subforms to display.
        </Typography>
      </div>
    );
  }
  return (
    <>
      <div className={styles.verticalTabsContainer}>
        <div className={styles.verticalTabItemsContainer}>
          <Typography className={styles.verticalTabHeader}>
            {headerTitle}
          </Typography>
          {formsToRender.map((form) => (
            <SubFormTab
              key={form.id}
              form={form}
              isActive={activeTab === form.id}
              onClick={onTabChange}
              isLoading={isLoading}
              prevComments={prevComments}
              errors={errors}
            />
          ))}
        </div>
        
        <div className={styles.formSectionStyleContainer}>
          {showRequestBadges && (
            <RequestBadges 
              referenceNumber={requestDetails?.reference_number} 
              requestProgressId={requestProgressId} 
              persona={persona} 
              requestId={requestId}
            />
          )}
      
          {/* hide title header for custom forms as they have their own internal headers */}
          {!isCustomForm && (
            <Typography
              variant="title1"
              component="h2"
              sx={{
                fontWeight: 500,
                marginBottom: 3,
                color: "#333",
                paddingBottom: 1,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              {formsToRender?.find(form => form.id === activeTab)?.name}
            </Typography>
          )}
        
          
          {customContent ? (
            children
          ) : isCustomForm ? (
            <React.Suspense fallback={<div>Loading...</div>}>
                <CustomFormRenderer
                  activeFormId={activeForm.id}
                  editable={editable}
                  isSendbackMode={isSendbackMode}
                  requestDetails={requestDetails}
                  persona={persona}
                  roleName={roleName}
                  requestProgress={requestProgress}
                  requestId={requestId}
                  customFormApprovalProps={customFormApprovalProps}
                  ProductDetails={ProductDetails}
                  ProductDetailsView={ProductDetailsView}
                  ManufacturersSite={ManufacturersSite}
                  ManufacturersSiteView={ManufacturersSiteView}
                />
            </React.Suspense>
          ) : editable ? (
            <>
              <EditableFormContent 
                forms={formsToRender} 
                activeTab={activeTab} 
                filledForm={filledForm}
                handleAddComment={handleAddComment}
                requestProgressId={requestProgressId}
                persona={persona}
                requestId={requestId}
                enableAddComment={enableAddComment}
                children={children}
                requestProgress={requestProgress}
                disabled={false}
                onFormDataChange={onFormDataChange}
                errors={errors}
              >
                {subformActionButtons}
              </EditableFormContent>
            </>
          ) : (
            <>
              <FormContent 
                forms={formsToRender} 
                activeTab={activeTab} 
                filledForm={filledForm}
                handleAddComment={handleAddComment}
                requestProgressId={requestProgressId}
                persona={persona}
                requestId={requestId}
                enableAddComment={enableAddComment}
                children={children}
                requestProgress={requestProgress}
                errors={errors}
              >
                {subformActionButtons}
              </FormContent>
            </>
          )}
        </div>
      </div>
      
      {formActionButtons && (
        <div className={styles.formLevelActionsContainer}>
          {formActionButtons}
        </div>
      )}
    </>
  );
};

// Export EditableFormContent at the end of the file

// Helper to extract form values from a rendered form
const extractFormValues = (container, subformId) => {
  if (!container || !document.contains(container)) {
    console.warn("Container not found or not in document");
    return {};
  }
  
  const formValues = {};
  
  try {
    const inputs = $(container).find(":input");
    
    inputs.each(function () {
      try {
        // Check if element still exists in DOM
        if (!this || !document.contains(this)) return;
        
        const inputType = $(this).attr("type");
        const name = $(this).attr("name");
        if (!name) return;
        
        switch (inputType) {
          case "checkbox":
            if (!formValues[name]) formValues[name] = [];
            if ($(this).is(":checked")) formValues[name].push($(this).val());
            break;
          case "radio":
            if ($(this).is(":checked")) formValues[name] = $(this).val();
            break;
          case "file":
            if (this.files) formValues[name] = this.files;
            break;
          default:
            const value = $(this).val();
            if (value !== undefined) formValues[name] = value;
        }
      } catch (err) {
        console.error("Error processing input:", err);
      }
    });
    
    // Handle select elements
    $(container).find("select").each(function () {
      try {
        if (!this || !document.contains(this)) return;
        
        const name = $(this).attr("name");
        if (!name) return;
        
        $(this).attr("multiple");
        const value = $(this).val();
        if (value !== undefined && value !== null) {
          formValues[name] = value;
        }
      } catch (err) {
        console.error("Error processing select:", err);
      }
    });
  } catch (error) {
    console.error("Error extracting form values:", error);
  }
  
  return formValues;
};

const EditableFormContentBase = ({ 
  forms, 
  activeTab, 
  filledForm,
  handleAddComment,
  requestProgressId,
  persona,
  requestId,
  enableAddComment = true,
  children,
  requestProgress,
  disabled = false,
  onFormDataChange,
  errors = null,
}) => {
  const containerRef = useRef(null);
  const { renderForm } = useFormRenderer();
  const lastActiveTabRef = useRef(activeTab);
  const renderedFormIdRef = useRef(null);
  const formDataCacheRef = useRef({});
  const { data: currentStageStatus } = useCurrentStageStatus(requestId, persona);

  // Find the active form
  const activeForm = forms.find(form => form.id === activeTab);

  useEffect(() => {
    if (!activeForm || !containerRef.current || !filledForm) return;

    const currentContainer = containerRef.current;
    
    // Save current form data before switching tabs
    if (lastActiveTabRef.current !== activeTab && !disabled) {
      const previousTabId = lastActiveTabRef.current;
      if (previousTabId && containerRef.current && containerRef.current.hasChildNodes()) {
        try {
          const previousFormData = extractFormValues(containerRef.current, previousTabId);
          if (Object.keys(previousFormData).length > 0) {
            formDataCacheRef.current[previousTabId] = previousFormData;
          }
        } catch (error) {
          console.error("Error saving form data:", error);
        }
      }
    }
    
    lastActiveTabRef.current = activeTab;
    
    // Only re-render if we're switching to a different form
    if (renderedFormIdRef.current === activeForm.id) {
      return; // Skip re-rendering if it's the same form
    }
    
    renderedFormIdRef.current = activeForm.id;
    
    // Prepare form data - merge original data with cached changes
    let mergedFilledForm = filledForm;
    if (!disabled && formDataCacheRef.current[activeTab]) {
      try {
        const originalData = filledForm?.value ? JSON.parse(filledForm.value) : {};
        const mergedData = {
          ...originalData,
          [activeTab]: {
            ...originalData[activeTab],
            ...formDataCacheRef.current[activeTab]
          }
        };
        mergedFilledForm = { ...filledForm, value: JSON.stringify(mergedData) };
      } catch (error) {
        console.error("Error merging form data:", error);
      }
    }
    
    // Clear and re-render the form
    currentContainer.innerHTML = '';

    renderForm({
      form: activeForm,
      container: currentContainer,
      filledForm: mergedFilledForm,
      handleAddComment,
      requestProgressId,
      persona,
      requestId,
      enableAddComment,
      showCommentsTitle: false,
      requestProgress,
      children,
      disabled,
      currentStageStatus,
    });

    // Add field errors and real-time validation after form rendering
    setTimeout(() => {
      if (errors?.subforms?.[activeForm.id]?.fields) {
        displayFieldErrors(currentContainer, activeForm.id, errors.subforms[activeForm.id].fields);
      }
      // Always attach validation listeners for real-time feedback
      attachRealTimeValidationListeners(currentContainer, activeForm.id);
    }, 100);
    
  }, [activeTab, activeForm, children, currentStageStatus, disabled, enableAddComment, filledForm, handleAddComment, persona, renderForm, requestId, requestProgress, requestProgressId, errors]);
  
  // Expose method to get all cached data
  React.useEffect(() => {
    if (!disabled && typeof onFormDataChange === 'function') {
      // Instead of using onFormDataChange for real-time updates,
      // we'll use it to pass a function that can get all form data
      onFormDataChange(() => {
        // Save current tab data first
        if (containerRef.current && activeTab) {
          try {
            const currentFormData = extractFormValues(containerRef.current, activeTab);
            if (Object.keys(currentFormData).length > 0) {
              formDataCacheRef.current[activeTab] = currentFormData;
            }
          } catch (error) {
            console.error("Error saving current form data:", error);
          }
        }
        return formDataCacheRef.current;
      });
    }
  }, [activeTab, disabled, onFormDataChange]);
  
  return (
    <>
      <div className="vertical-tab-panel">
        <div
          className="form-section-container"
          ref={containerRef}
        />
      </div>
      {children}
    </>
  );
};

export const EditableFormContent = EditableFormContentBase;

// Generic utility functions for real-time validation (DRY principle)

// Generic function to display field errors for any form type
export const displayGenericFieldErrors = (container, formType, fieldErrors) => {
  if (!container || !fieldErrors) return;
  
  Object.keys(fieldErrors).forEach(fieldName => {
    try {
      const fieldError = fieldErrors[fieldName];
      if (!fieldError || !fieldError.error) return;
      
      // Find input by name, id, or data attribute
      let input = $(container).find(`[name="${fieldName}"]`).first();
      if (input.length === 0) {
        input = $(container).find(`#${fieldName}`).first();
      }
      if (input.length === 0) {
        input = $(container).find(`[data-field="${fieldName}"]`).first();
      }
      
      if (input.length > 0) {
        // Remove any existing error message for this field
        input.closest('.form-group, .form-field, .field-wrapper, .MuiFormControl-root')
              .find('.field-error-message').remove();
        
        // Create error message element with escaped content
        const errorText = String(fieldError.error).replace(/[<>&"']/g, (match) => {
          const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
          return entities[match];
        });
        
        const errorElement = $(`
          <div class="field-error-message" data-field="${fieldName}" style="
            color: #d32f2f;
            font-size: 12px;
            margin-top: 4px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 4px;
            line-height: 1.4;
          ">
            <span style="color: #d32f2f; flex-shrink: 0;">⚠</span>
            <span>${errorText}</span>
          </div>
        `);
        
        // Insert error message - try different positioning strategies
        const formGroup = input.closest('.form-group, .form-field, .field-wrapper, .MuiFormControl-root');
        if (formGroup.length > 0) {
          formGroup.append(errorElement);
        } else {
          const parentDiv = input.parent('div').length > 0 ? input.parent('div') : input;
          parentDiv.after(errorElement);
        }
        
        // Add error styling to input
        input.css({
          'border-color': '#d32f2f',
          'box-shadow': '0 0 0 1px rgba(211, 47, 47, 0.2)'
        }).addClass('form-field-error');
      }
    } catch (error) {
      console.warn('Error displaying field error for', fieldName, error);
    }
  });
};

// Generic function to clear field errors for any form type
export const clearGenericFieldErrors = (container, fieldName = null) => {
  if (!container) return;
  
  try {
    if (fieldName) {
      // Clear specific field error
      $(container).find(`.field-error-message[data-field="${fieldName}"]`).remove();
      const input = $(container).find(`[name="${fieldName}"], #${fieldName}, [data-field="${fieldName}"]`).first();
      if (input.length > 0) {
        input.css({
          'border-color': '',
          'box-shadow': ''
        }).removeClass('form-field-error');
      }
    } else {
      // Clear all field errors in container
      $(container).find('.field-error-message').remove();
      $(container).find('.form-field-error').each(function() {
        $(this).css({
          'border-color': '',
          'box-shadow': ''
        }).removeClass('form-field-error');
      });
    }
  } catch (error) {
    console.warn('Error clearing field errors:', error);
  }
};

// Generic function to attach real-time validation to any form type
export const attachGenericRealTimeValidation = (container, formType, validateFn, clearFieldErrorFn, addFieldErrorFn) => {
  if (!container || !formType || typeof validateFn !== 'function') return;
  
  $(container).find('input, textarea, select').each(function() {
    const input = $(this);
    const fieldName = input.attr('name') || input.attr('id') || input.attr('data-field');
    
    if (fieldName) {
      // Remove existing listeners to avoid duplicates
      input.off('input.genericValidation change.genericValidation blur.genericValidation focus.genericValidation');
      
      let hasBeenTouched = false;
      
      input.on('focus.genericValidation', function() {
        hasBeenTouched = true;
      });
      
      input.on('input.genericValidation change.genericValidation blur.genericValidation', function(e) {
        try {
          if (!hasBeenTouched && e.type !== 'blur') {
            return;
          }
          
          // Get current form data
          const formData = {};
          $(container).find('input, textarea, select').each(function() {
            const field = $(this);
            const name = field.attr('name') || field.attr('id') || field.attr('data-field');
            if (name) {
              const value = field.val();
              if (field.attr('type') === 'checkbox') {
                formData[name] = field.is(':checked');
              } else if (field.attr('type') === 'file') {
                formData[name] = field[0].files ? Array.from(field[0].files) : [];
              } else {
                formData[name] = value;
              }
            }
          });
          
          // Validate current field only
          const validationResult = validateFn(formData);
          const fieldError = validationResult.errors?.fields?.[fieldName];
          const currentlyHasError = input.hasClass('form-field-error');
          
          if (fieldError && !currentlyHasError) {
            // Show error
            displayGenericFieldErrors(container, formType, { [fieldName]: fieldError });
            if (addFieldErrorFn) {
              addFieldErrorFn(formType, fieldName, fieldError);
            }
          } else if (!fieldError && currentlyHasError) {
            // Clear error
            clearGenericFieldErrors(container, fieldName);
            if (clearFieldErrorFn) {
              clearFieldErrorFn(formType, fieldName);
            }
          }
        } catch (error) {
          console.warn('Error in generic field validation for', fieldName, error);
        }
      });
    }
  });
};

// Custom React hook for integrating real-time validation with any form type (DRY principle)
export const useRealTimeValidation = (formType, validateFn, containerRef, errors) => {
  let clearGenericFieldError, addGenericFieldError;
  
  try {
    const multiFormContext = useMultiForm();
    clearGenericFieldError = multiFormContext?.clearGenericFieldError;
    addGenericFieldError = multiFormContext?.addGenericFieldError;
  } catch (e) {
    // Hook not available - fallback to window methods
    clearGenericFieldError = window.clearGenericFieldError;
    addGenericFieldError = window.addGenericFieldError;
  }
  
  useEffect(() => {
    const container = containerRef?.current;
    if (!container || !formType || !validateFn) return;
    
    // Display existing errors
    if (errors?.fields) {
      displayGenericFieldErrors(container, formType, errors.fields);
    }
    
    // Attach real-time validation
    attachGenericRealTimeValidation(
      container, 
      formType, 
      validateFn,
      clearGenericFieldError,
      addGenericFieldError
    );
    
    return () => {
      // Cleanup listeners when component unmounts
      if (container) {
        $(container).find('input, textarea, select').off('.genericValidation');
      }
    };
  }, [formType, validateFn, containerRef, errors, clearGenericFieldError, addGenericFieldError]);
};

// Export helper function for form value extraction
export { extractFormValues }; 